import { getApiBaseUrl } from '../../../utils/apiConfig';

// Types for forms
export interface Form {
  id: string;
  name: string;
  description?: string;
  fields: FormField[];
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface FormField {
  id: string;
  type: 'text' | 'textarea' | 'select' | 'radio' | 'checkbox' | 'number' | 'email' | 'date' | 'document' | 'paragraph' | 'header';
  label: string;
  name: string;
  placeholder?: string;
  helpText?: string;
  required: boolean;
  options?: any[]; // For select, radio, checkbox
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
    message?: string;
  };
}

// Helper function to generate IDs
const generateId = (): string => {
  return 'form_' + Math.random().toString(36).substring(2, 15) + 
         Math.random().toString(36).substring(2, 15);
};

/**
 * Save form to backend (create new or update existing)
 */
export const saveForm = async (formInput: { id?: string; name: string; description?: string; fields: FormField[] }, userId: string): Promise<Form> => {
  const now = new Date().toISOString();
  
  try {
    // Determine if this is an update (has ID) or create (no ID)
    const isUpdate = !!formInput.id;
    const apiUrl = isUpdate 
      ? `${getApiBaseUrl()}/api/ai-sequence/forms/${formInput.id}`
      : `${getApiBaseUrl()}/api/ai-sequence/forms`;
    
    const requestBody = {
      name: formInput.name,
      description: formInput.description,
      fields: formInput.fields
    };
    
    // Add created_by only for new forms
    if (!isUpdate) {
      (requestBody as any).created_by = userId;
    }
    
    const response = await fetch(apiUrl, {
      method: isUpdate ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody)
    });
    
    if (!response.ok) {
      throw new Error(`Failed to ${isUpdate ? 'update' : 'create'} form on backend`);
    }
    
    const data = await response.json();
    
    if (data.success && data.data) {
      return data.data;
    }
    
    throw new Error('Invalid response format from API');
  } catch (error) {
    console.error('Error saving form:', error);
    throw new Error(`Failed to ${formInput.id ? 'update' : 'create'} form`);
  }
};

/**
 * Get all forms for a user
 */
export const getUserForms = async (userId: string): Promise<Form[]> => {
  try {
    const apiUrl = `${getApiBaseUrl()}/api/ai-sequence/forms?userId=${userId}`;
    const response = await fetch(apiUrl);
    
    if (!response.ok) {
      throw new Error('Failed to fetch forms from backend');
    }
    
    const data = await response.json();
    
    if (data.success && Array.isArray(data.data)) {
      return data.data;
    }
    
    return [];
  } catch (error) {
    console.error('Error getting user forms:', error);
    throw new Error('Failed to fetch forms');
  }
};

/**
 * Get form by ID
 */
export const getFormById = async (formId: string): Promise<Form | null> => {
  try {
    const apiUrl = `${getApiBaseUrl()}/api/ai-sequence/forms/${formId}`;
    const response = await fetch(apiUrl);
    
    if (!response.ok) {
      if (response.status === 404) {
      return null;
    }
      throw new Error('Failed to fetch form from backend');
    }
    
    const data = await response.json();
    
    if (data.success && data.data) {
      return data.data;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting form by ID:', error);
    return null;
  }
};

/**
 * Update form
 */
export const updateForm = async (formId: string, updates: Partial<Form>): Promise<Form | null> => {
  try {
    const apiUrl = `${getApiBaseUrl()}/api/ai-sequence/forms/${formId}`;
    const response = await fetch(apiUrl, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...updates, updatedAt: new Date().toISOString() })
    });
    
    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      throw new Error('Failed to update form on backend');
    }
    
    const data = await response.json();
    
    if (data.success && data.data) {
      return data.data;
    }
    
    return null;
  } catch (error) {
    console.error('Error updating form:', error);
    throw new Error('Failed to update form');
  }
};

/**
 * Delete form
 */
export const deleteForm = async (formId: string): Promise<void> => {
  try {
    const apiUrl = `${getApiBaseUrl()}/api/ai-sequence/forms/${formId}`;
    const response = await fetch(apiUrl, { method: 'DELETE' });
    
    if (!response.ok) {
      throw new Error('Failed to delete form from backend');
    }
  } catch (error) {
    console.error('Error deleting form:', error);
    throw new Error('Failed to delete form');
  }
};

/**
 * Duplicate an existing form
 */
export const duplicateForm = async (sourceFormId: string, newName: string, userId: string): Promise<Form> => {
  try {
    const sourceForm = await getFormById(sourceFormId);
    if (!sourceForm) {
      throw new Error('Source form not found');
    }
    
    const apiUrl = `${getApiBaseUrl()}/api/ai-sequence/forms/${sourceFormId}/duplicate`;
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ newName, userId })
    });
    
    if (!response.ok) {
      throw new Error('Failed to duplicate form on backend');
    }
    
    const data = await response.json();
    
    if (data.success && data.data) {
      return data.data;
    }
    
    throw new Error('Invalid response format from API');
  } catch (error) {
    console.error('Error duplicating form:', error);
    throw new Error('Failed to duplicate form');
  }
}; 